import { Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { ModalBodyClass } from '../../../../../modals/modal-body';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { AutoAttendantHoliday, DateTimeSpan } from '../../../../../shared/models/generated/ziroModels';
import { ZiroFormsModule } from '../../../../../forms/ziro-forms.module';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonSizes, ButtonStyles } from '../../../../../button/button.component';
import { ZiroIcons } from '../../../../../shared/models/ziro-icons.enum';
import {
  DateRangesConfig,
  ZiroDateRangesComponent,
} from '../../../../../forms/fields/date-range/ziro-date-ranges.component';
import { TeamsAutoAttendantsHolidayResource } from '../../../../resources/teams-auto-attendants-holiday.resource';
import { DetailedModalComponent } from '../../../../../modals/detailed-modal/detailed-modal.component';

export interface CreateHolidayFormData {
  name: string;
  dateTimeRanges: DateTimeSpan[];
}

@Component({
  selector: 'ziro-create-holiday-modal',
  imports: [ZiroFormsModule, TranslateModule],
  templateUrl: './create-holiday-modal.component.html',
  providers: [TeamsAutoAttendantsHolidayResource],
})
export class CreateHolidayModalComponent extends ModalBodyClass<CreateHolidayFormData> implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private _modalComponent = inject(DetailedModalComponent<any>);

  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;
  private _ziroDateRangesComponent?: ZiroDateRangesComponent;

  @ViewChild(ZiroDateRangesComponent)
  set ziroDateRangesComponent(component: ZiroDateRangesComponent | undefined) {
    if (!component || this._ziroDateRangesComponent === component) {
      return;
    }
    this._ziroDateRangesComponent = component;
    this._dateRangesSub.unsubscribe();
    this._dateRangesSub = component.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<DateTimeSpan[]>) => {
      this.onRangesUpdate(update);
    });
    this.setDisplayFormValidationState();
  }

  get ziroDateRangesComponent(): ZiroDateRangesComponent | undefined {
    return this._ziroDateRangesComponent;
  }

  dateTimeRanges = signal<DateTimeSpan[]>([{ start: '', end: '' }]);

  formConfig: ZiroFormConfig = {
    fields: {
      name: {
        label: () => 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday_schedule.name.label',
        dataAutomation: 'create-holiday-name',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: () => true,
      },
      dateTimeRanges: {
        label: () => 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.date_ranges.label',
        dataAutomation: 'create-holiday-date-ranges',
        componentConfig: new DateRangesConfig(),
        required: () => true,
        validation: [
          {
            validator: (ranges: DateTimeSpan[]): ZiroFormsValidationState => {
              if (!ranges?.length) {
                return ZiroFormsValidationState.INVALID;
              }

              return ranges.some((range) => !range.start || !range.end || range.end <= range.start)
                ? ZiroFormsValidationState.INVALID
                : ZiroFormsValidationState.VALID;
            },
            message: () => 'tkey;validators.global.required.missing.error',
          },
        ],
      },
    },
  };
  childFormValidationState: ZiroFormsValidationState;

  private _dateRangesSub = new Subscription();

  ngOnInit(): void {
    const existing: AutoAttendantHoliday = this._modalComponent.modalViewProperties?.existingHoliday;
    const allHolidays: AutoAttendantHoliday[] = this._modalComponent.modalViewProperties?.allHolidays ?? [];

    this.formConfig.fields['name'].validation = [
      {
        validator: (name: string) => {
          const trimmed = name?.trim().toLowerCase();
          const isDuplicate = allHolidays.some(
            (h) => h.name.toLowerCase() === trimmed && h.name.toLowerCase() !== existing?.name?.toLowerCase(),
          );
          return isDuplicate ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
        },
        message: () => 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.name.duplicate',
      },
    ];
    if (existing) {
      this.dateTimeRanges.set(existing.dateTimeRanges ?? [{ start: '', end: '' }]);
      this.entitySource.next({ name: existing.name, dateTimeRanges: existing.dateTimeRanges });
    } else {
      this.entitySource.next({ name: '', dateTimeRanges: [{ start: '', end: '' }] });
    }
  }

  ngOnDestroy(): void {
    this._dateRangesSub.unsubscribe();
  }

  onRangesUpdate(update: ZiroFormsUpdate<DateTimeSpan[]>): void {
    this.dateTimeRanges.set(update.new ?? []);
    this.childFormValidationState = update.valid as ZiroFormsValidationState;
  }

  setDisplayFormValidationState(): void {
    const dateTimeRanges = this.formData.dateTimeRanges ?? [];
    this.childFormValidationState = dateTimeRanges.some(
      (range) => !range.start || !range.end || range.end <= range.start,
    )
      ? ZiroFormsValidationState.INVALID
      : ZiroFormsValidationState.VALID;
  }

  submit(): Observable<AutoAttendantHoliday | null> {
    this.isFormSubmitted = true;
    const dateTimeRanges = this.formData.dateTimeRanges ?? [];
    if (!this.isFormValid()) {
      return EMPTY;
    } else {
      const existing: AutoAttendantHoliday = this._modalComponent.modalViewProperties?.existingHoliday;
      const holiday: AutoAttendantHoliday = {
        id: existing?.id ?? null,
        name: this.entity.name.trim(),
        dateTimeRanges,
      };
      return this._modalComponent.modalViewProperties.submitFn(holiday);
    }
  }

  isFormValid(): boolean {
    this.ziroDateRangesComponent?.childFormComponent?.fieldComponents?.forEach((field) => {
      field.showValidation = true;
    });
    const dateTimeRanges = this.formData.dateTimeRanges ?? [];
    const hasInvalidDateRange = dateTimeRanges.some((range) => {
      return !range.start || !range.end || range.end <= range.start;
    });
    this.childFormValidationState = hasInvalidDateRange
      ? ZiroFormsValidationState.INVALID
      : ZiroFormsValidationState.VALID;
    return !hasInvalidDateRange;
  }

  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly validationStates = ZiroFormsValidationState;
}
